import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import * as CONST from "../config/Constant";
import { IconMessageChatbot, IconSend, IconChevronDown, IconRobot, IconUser, IconHeadset } from "@tabler/icons-react";
import "./Chatbot.css";

const Chatbot = ({ Query }) => {
    const [authUserData] = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState([
        { role: "assistant", content: "Hello! I'm your AI Assistant. How can I help you today?", timestamp: new Date() }
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMessage = { role: "user", content: input, timestamp: new Date() };
        setMessages(prev => [...prev, userMessage]);
        setInput("");
        setIsLoading(true);

        try {
            const token = authUserData?.[0]?.token;
            const response = await axios.post(
                CONST[Query],
                { query: input },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            if (response.data.status === "success") {
                const botMessage = {
                    role: "assistant",
                    content: response.data.data,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, botMessage]);
            } else {
                setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble processing that right now.", timestamp: new Date() }]);
            }
        } catch (error) {
            console.error("Chatbot Error:", error);
            const errorMsg = error.response ? "The server encountered an error. Please try again later." : error.message;
            setMessages(prev => [...prev, { role: "assistant", content: `Oops! ${errorMsg}`, timestamp: new Date() }]);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleChat = () => setIsOpen(!isOpen);

    return (
        <div className={`chatbot-container ${isOpen ? 'open' : ''}`}>
            {!isOpen && (
                <button className="chatbot-toggle" onClick={toggleChat} title="Ask Assistant">
                    <IconMessageChatbot size={32} />
                </button>
            )}

            {isOpen && (
                <div className="chatbot-window">
                    <div className="chatbot-header">
                        <div className="chatbot-header-info">
                            <div className="bg-white p-2 rounded-3 me-2 mr-2">
                                <IconHeadset size={20} className="text-primary" style={{ color: '#1167e8' }} />
                            </div>
                            <div>
                                <span className="d-block lh-1">AI Assistant</span>
                                <small className="text-white-50 d-flex align-items-center" style={{ fontSize: '0.7rem' }}>
                                    Online <span className="chatbot-status ms-1"></span>
                                </small>
                            </div>
                        </div>
                        <button className="chatbot-close" onClick={toggleChat}>
                            <IconChevronDown size={20} />
                        </button>
                    </div>

                    <div className="chatbot-messages">
                        {messages.map((msg, index) => (
                            <div key={index} className={`message-wrapper ${msg.role}`}>
                                <div className="message-icon">
                                    {msg.role === 'assistant' ? <IconRobot size={18} /> : <IconUser size={18} />}
                                </div>
                                <div className="message-content">
                                    {msg.content}
                                    <span className="message-time">
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="message-wrapper assistant">
                                <div className="message-icon"><IconRobot size={18} /></div>
                                <div className="message-content dot-typing-container">
                                    <div className="dot-typing">
                                        <div className="dot"></div>
                                        <div className="dot"></div>
                                        <div className="dot"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form className="chatbot-input-area" onSubmit={handleSend}>
                        <input
                            type="text"
                            placeholder="Ask me anything..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={isLoading}
                        />
                        <button type="submit" disabled={!input.trim() || isLoading} className="shadow-sm">
                            <IconSend size={20} />
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Chatbot;